import { z, type ZodType } from 'zod';

import { jsonRequest, resolveApiFetch } from './shared';
import { assertOk } from './response';

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
}

async function requestJson<T>(path: string, { method = 'GET', body, signal }: RequestOptions = {}): Promise<T> {
  return jsonRequest<T>(path, {
    method,
    body,
    signal,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    schema: z.any() as ZodType<T>,
  });
}

export type CateringEventStatus =
  | 'draft'
  | 'proposal'
  | 'tentative'
  | 'confirmed'
  | 'in_fulfillment'
  | 'completed'
  | 'cancelled';

export type CateringEventServiceStyle =
  | 'on_premise'
  | 'offsite_full_service'
  | 'offsite_dropoff'
  | 'pickup';

export type CateringEventOrderStatus =
  | 'pending'
  | 'scheduled'
  | 'in_progress'
  | 'fulfilled'
  | 'cancelled'
  | 'refunded';

export type CateringEventOrderKind = 'internal' | 'external' | 'deposit' | 'balance' | 'add_on';

export interface CateringEventOrderSnapshot {
  id: string;
  code: string;
  status: string;
  totalCents: number;
  type: string;
  placedAt: number;
}

export interface CateringEventOrderLink {
  id: string;
  eventId: string;
  orderId: string | null;
  kind: CateringEventOrderKind;
  status: CateringEventOrderStatus;
  amountCents: number | null;
  currency: string;
  dueAt: number | null;
  settledAt: number | null;
  dueStatus: 'dueSoon' | 'overdue' | null;
  externalReference: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  orderSnapshot: CateringEventOrderSnapshot | null;
  createdAt: number;
  updatedAt: number;
  paymentIntentId: string | null;
  paymentStatus: string | null;
  paymentClientSecret: string | null;
}

export type CateringFulfillmentHealth = 'onTrack' | 'attentionNeeded' | 'blocked';

export interface CateringEventFulfillmentSummary {
  totalMilestones: number;
  completed: number;
  open: number;
  cancelled: number;
  refunded: number;
  dueSoon: number;
  overdue: number;
  nextDueAt: number | null;
  health: CateringFulfillmentHealth;
}

export interface CateringCollectPaymentPayload {
  paymentMethodId?: string;
  paymentIntentId?: string;
  captureMethod?: 'automatic' | 'manual';
  customerEmail?: string | null;
}

export interface CateringPaymentResult {
  id: string;
  status: string;
  clientSecret: string | null;
  requiresAction: boolean;
  lastError: string | null;
}

export interface CateringEventSummary {
  id: string;
  name: string;
  internalCode: string | null;
  status: CateringEventStatus;
  serviceStyle: CateringEventServiceStyle;
  eventDate: number;
  serviceWindowStart: number | null;
  serviceWindowEnd: number | null;
  estimatedGuests: number | null;
  actualGuests: number | null;
  budgetCents: number | null;
  currency: string;
  depositDueAt: number | null;
  finalPaymentDueAt: number | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  leadSource: string | null;
  notes: string | null;
  requirements: Record<string, unknown>;
  timeline: Array<Record<string, unknown>>;
  metadata: Record<string, unknown>;
  locationId: string | null;
  customerProfileId: string | null;
  createdByStaffId: string | null;
  updatedByStaffId: string | null;
  createdAt: number;
  updatedAt: number;
  orderCount: number;
  scheduleSummary: {
    durationMinutes: number;
    prepWindowMinutes: number;
    serviceWindowMinutes: number;
  };
  staffing: {
    total: number;
    bartenders: number;
    servers: number;
    support: number;
  };
  costEstimate: {
    revenueCents: number;
    laborCents: number;
    marginCents: number;
  };
  fulfillment: CateringEventFulfillmentSummary;
}

export interface CateringEventDetail extends CateringEventSummary {
  orders: CateringEventOrderLink[];
}

export interface CateringEventMetrics {
  planning: number;
  confirmed: number;
  paymentsDueSoon: number;
  paymentsOverdue: number;
}

export interface ListCateringEventsResponse {
  ok: boolean;
  events: CateringEventSummary[];
  metrics: CateringEventMetrics;
}

export interface GetCateringEventResponse {
  ok: boolean;
  event: CateringEventDetail;
}

export interface CateringMilestonePayload {
  kind?: CateringEventOrderKind;
  status?: CateringEventOrderStatus;
  amountCents?: number | null;
  currency?: string | null;
  dueAt?: number | null;
  externalReference?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CreateCateringEventPayload {
  name: string;
  eventDate: number;
  serviceWindowStart?: number | null;
  serviceWindowEnd?: number | null;
  serviceStyle?: CateringEventServiceStyle;
  status?: CateringEventStatus;
  estimatedGuests?: number | null;
  actualGuests?: number | null;
  budgetCents?: number | null;
  currency?: string;
  depositDueAt?: number | null;
  finalPaymentDueAt?: number | null;
  internalCode?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  leadSource?: string | null;
  notes?: string | null;
  requirements?: Record<string, unknown>;
  timeline?: Array<Record<string, unknown>>;
  metadata?: Record<string, unknown>;
  locationId?: string | null;
  customerProfileId?: string | null;
  milestones?: CateringMilestonePayload[];
}

export type UpdateCateringEventPayload = Partial<CreateCateringEventPayload> & {
  status?: CateringEventStatus;
  serviceStyle?: CateringEventServiceStyle;
  milestones?: never;
};

export type UpdateCateringOrderPayload = Partial<{
  orderId: string | null;
  kind: CateringEventOrderKind;
  status: CateringEventOrderStatus;
  amountCents: number | null;
  currency: string | null;
  dueAt: number | null;
  externalReference: string | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
}>;

export interface ListCateringEventsParams {
  status?: CateringEventStatus;
  serviceStyle?: CateringEventServiceStyle;
  search?: string;
  limit?: number;
  before?: number | null;
  after?: number | null;
}

const buildQueryString = (params?: ListCateringEventsParams): string => {
  if (!params) {
    return '';
  }
  const searchParams = new URLSearchParams();
  if (params.status) {
    searchParams.set('status', params.status);
  }
  if (params.serviceStyle) {
    searchParams.set('serviceStyle', params.serviceStyle);
  }
  if (params.search) {
    searchParams.set('search', params.search);
  }
  if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
    searchParams.set('limit', String(Math.max(1, Math.min(200, Math.floor(params.limit)))));
  }
  if (params.before !== undefined && params.before !== null && Number.isFinite(params.before)) {
    searchParams.set('before', String(params.before));
  }
  if (params.after !== undefined && params.after !== null && Number.isFinite(params.after)) {
    searchParams.set('after', String(params.after));
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

export async function listCateringEvents(
  params?: ListCateringEventsParams,
  signal?: AbortSignal,
): Promise<ListCateringEventsResponse> {
  const query = buildQueryString(params);
  return requestJson<ListCateringEventsResponse>(`/catering/events${query}`, { signal });
}

export async function getCateringEvent(
  eventId: string,
  signal?: AbortSignal,
): Promise<GetCateringEventResponse> {
  if (!eventId) {
    throw new Error('eventId is required');
  }
  return requestJson<GetCateringEventResponse>(`/catering/events/${eventId}`, { signal });
}

export async function createCateringEvent(
  payload: CreateCateringEventPayload,
): Promise<{ ok: boolean; event: CateringEventSummary }> {
  return requestJson<{ ok: boolean; event: CateringEventSummary }>('/catering/events', {
    method: 'POST',
    body: payload,
  });
}

export async function updateCateringEvent(
  eventId: string,
  payload: UpdateCateringEventPayload,
): Promise<{ ok: boolean; event: CateringEventSummary }> {
  return requestJson<{ ok: boolean; event: CateringEventSummary }>(`/catering/events/${eventId}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function createCateringEventMilestone(
  eventId: string,
  payload: CateringMilestonePayload & { orderId?: string | null },
): Promise<{ ok: boolean; order: CateringEventOrderLink }> {
  return requestJson<{ ok: boolean; order: CateringEventOrderLink }>(`/catering/events/${eventId}/orders`, {
    method: 'POST',
    body: payload,
  });
}

export async function collectCateringEventPayment(
  eventId: string,
  linkId: string,
  payload: CateringCollectPaymentPayload,
): Promise<{ ok: boolean; payment: CateringPaymentResult; order: CateringEventOrderLink }> {
  return requestJson<{ ok: boolean; payment: CateringPaymentResult; order: CateringEventOrderLink }>(
    `/catering/events/${eventId}/orders/${linkId}/payment-intent`,
    {
      method: 'POST',
      body: payload,
    },
  );
}

export async function updateCateringEventMilestone(
  eventId: string,
  linkId: string,
  payload: UpdateCateringOrderPayload,
): Promise<{ ok: boolean; order: CateringEventOrderLink }> {
  return requestJson<{ ok: boolean; order: CateringEventOrderLink }>(
    `/catering/events/${eventId}/orders/${linkId}`,
    {
      method: 'PATCH',
      body: payload,
    },
  );
}

export async function deleteCateringEventMilestone(
  eventId: string,
  linkId: string,
): Promise<{ ok: boolean }> {
  return requestJson<{ ok: boolean }>(`/catering/events/${eventId}/orders/${linkId}`, {
    method: 'DELETE',
  });
}

export async function settleCateringEventMilestone(
  eventId: string,
  linkId: string,
): Promise<{ ok: boolean; order: CateringEventOrderLink }> {
  return requestJson<{ ok: boolean; order: CateringEventOrderLink }>(
    `/catering/events/${eventId}/orders/${linkId}/settle`,
    {
      method: 'POST',
    },
  );
}

export async function exportCateringEvents(params?: ListCateringEventsParams): Promise<Blob> {
  const query = buildQueryString(params);
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/catering/events/export${query}`, { method: 'GET' });
  await assertOk(response);
  return response.blob();
}
